import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { useIsClient } from "usehooks-ts";

import { ChatKind } from "@/api";
import { AiLogo } from "@/components/ai-logo/AiLogo";
import { useCreateHintHoverHandlers } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { ButtonShortcut } from "@/components/sidebar/Button";
import { ImageDndArea } from "@/components/tiptap/ImageDndArea";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { SHOW_CHAT_INPUT_ID, VisibilityToggle } from "@/components/VisibilityToggle";
import { useStore } from "@/store";
import { getLatestChat } from "@/store/ChatsStore";
import { useChats } from "@/store/useChats";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { ChatUnreadMessagesCount } from "./ChatUnreadMessagesCount";

export const ChatWithBotButton = observer(function ChatWithBotButton({
  isCollapsed,
}: {
  isCollapsed?: boolean;
}) {
  const { auth, doc, view } = useStore();
  const chats = useChats();
  const isClient = useIsClient();
  const t = useTranslations();
  const isChatOpened = chats.isOpenChat && chats.activeChatKind === ChatKind.personal;

  const { handlerProps, tooltip } = useTooltip({
    title: <ButtonShortcut text={t("chat.openChatWithBot")} hotkey="open-chat-with-bot" />,
    placement: "top-end",
    disabled: !isCollapsed,
  });
  const createHintHandlers = useCreateHintHoverHandlers();

  if (!isClient || !auth.isLoggedIn || !doc || !doc.permissions.comment || chats.botChatDisabled) {
    return null;
  }

  const handleClick = () => {
    trackEvent("chat_with_bot_opened", {
      source: "bottom_menu_button",
    });
    chats.openChat(true);
  };

  const handleFileDropped = async (files: File[]) => {
    await chats.ensureBotChatForCurrentDoc();
    const chat = getLatestChat(chats.chatsWithBot);
    if (!chat) return;

    trackEvent("chat_with_bot_opened", {
      source: "bottom_menu_button",
    });
    chats.openChat(true, chat.uid);
    await chat.uploadFiles(files);
  };

  return (
    <ImageDndArea
      className={twMerge("group/chat", isChatOpened && "hidden")}
      onFileDropped={(files) => {
        handleFileDropped(files).catch((err) => {
          console.error("Failed to upload chat files:", err);
        });
      }}
      placeholder={
        isCollapsed ? (
          <Icon name="Image" />
        ) : (
          <div className="text-document-text tracking-tight text-[17px]">
            {t("chat.dndPlaceholder")}
          </div>
        )
      }
    >
      <button
        className={twMerge(
          "w-full h-12 mb-2.5 p-2 rounded-12 flex chat-input-surface",
          isCollapsed && "w-11 p-0",
        )}
        type="button"
        onClick={handleClick}
        {...handlerProps}
        {...createHintHandlers("aiHelper")}
        aria-label={t("chat.openChatWithBot")}
      >
        <div className="flex items-center justify-center gap-1.5 w-full">
          <AiLogo state="idle" />
          {!isCollapsed && (
            <>
              <div className="text-gray-400 dark:text-gray-600 tracking-tight">
                {t("chat.chatWithBotPlaceholder")}
              </div>
              <div className="ml-auto w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-8 flex items-center justify-center">
                <Icon name="ArrowUp" className="[--color-icon-main:var(--color-icon-active)]" />
              </div>
            </>
          )}
        </div>
      </button>
      <VisibilityToggle
        id={SHOW_CHAT_INPUT_ID}
        className={twMerge(
          "absolute top-2.75 -left-7.75 z-1 rounded-l-6",
          // Hidden with opacity, not `display`, so it stays reachable by keyboard
          "opacity-0 pointer-events-none group-hover/chat:opacity-100 group-hover/chat:pointer-events-auto",
          "focus-visible:opacity-100 focus-visible:pointer-events-auto",
          "after:absolute after:inset-y-0 after:left-full after:w-px after:content-['']",
        )}
        label={t("chat.showChatInput")}
        onClick={() => view.toggleFloatingChatInput(false)}
      >
        <Icon name="HideSidebar" />
      </VisibilityToggle>
      <ChatUnreadMessagesCount
        className="absolute top-0 left-[21px]"
        count={chats.unreadMessagesCountForBotChat}
      />
      {tooltip}
    </ImageDndArea>
  );
});
